<script>
import { mapGetters } from 'vuex';
import { isWithinInterval, startOfDay, endOfDay } from 'date-fns';
import Translatable from '@src/mixins/translatable.js';

export default {
  mixins: [Translatable],
  computed: {
    ...mapGetters('currentNews', ['noticeList']),
    ...mapGetters('attractions', ['getAttractionId']),
    ...mapGetters('translations', ['currentLanguage']),

    actualNoticeList() {
      return this.noticeList.filter((notice) =>
        isWithinInterval(new Date(), {
          start: startOfDay(new Date(notice.startDate)),
          end: endOfDay(new Date(notice.endDate)),
        })
      );
    },
  },
  watch: {
    actualNoticeList: {
      immediate: true,
      handler(newList) {
        if (!newList || !newList.length) return;

        const missingIds = new Set();

        newList.forEach((notice) => {
          if (notice.attractions && Array.isArray(notice.attractions)) {
            notice.attractions.forEach((id) => {
              if (!this.getAttractionId(id)) {
                missingIds.add(id);
              }
            });
          }
        });

        if (missingIds.size > 0) {
          const promises = Array.from(missingIds).map((id) =>
            this.$store.dispatch('attractions/fetchAttractionById', id)
          );
          Promise.all(promises).catch((err) =>
            console.error('Error loading missing attractions:', err)
          );
        }
      },
    },
  },
  methods: {
    isSingleLink(notice) {
      const attractions = notice.attractions;

      if (!attractions || !Array.isArray(attractions)) return false;

      if (attractions.length === 1) {
        const id = attractions[0];
        return !!this.getAttractionId(id);
      }

      return false;
    },

    getAttractionLink(notice) {
      const attractionId = notice.attractions[0];
      const attraction = this.getAttractionId(attractionId);

      if (!attraction) return '/';

      const slugs = attraction.slugs || attraction.urlSlug || {};
      const targetSlug = slugs[this.currentLanguage] || slugs.en || attraction.slug;

      return this.$l('attractions_detail', { slug: targetSlug });
    },
  },
};
</script>

<template>
  <div class="content">
    <div
      v-for="notice in actualNoticeList"
      :key="notice._id"
      class="card"
      :class="{ 'not-assigned': !isSingleLink(notice) }"
    >
      <div class="title-container">
        <router-link v-if="isSingleLink(notice)" :to="getAttractionLink(notice)" class="link">
          <p class="title">
            {{ getTranslatedContent('title', notice.content) }}
          </p>
        </router-link>

        <p v-else class="title not-assigned">
          {{ getTranslatedContent('title', notice.content) }}
        </p>

        <p class="date">{{ notice.startDate | ddmmYYYY }} - {{ notice.endDate | ddmmYYYY }}</p>
      </div>

      <div class="text-container">
        <div class="text" v-html="getTranslatedContent('text', notice.content)"></div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@design';

.link {
  text-decoration: none;
  display: block;
}

.content {
  display: flex;
  width: 1140px;
  flex-wrap: wrap;
}

.card {
  width: 100%;
  display: flex;
  justify-content: space-between;
  min-height: 90px;
  height: auto;
  border-bottom: 1px solid $dark-gray;
  padding: 33px 0 17px 28px;

  &.not-assigned {
    cursor: default;
  }
}

.title-container {
  width: 254px;
}

.title {
  font-family: Open Sans Bold, sans-serif;
  font-weight: bold;
  text-transform: uppercase;
  font-style: normal;
  font-size: 18px;
  line-height: 23px;
  color: $black;
  transition: color 0.2s ease;

  cursor: pointer;

  &:hover {
    color: $gray;
  }

  &.not-assigned {
    cursor: default;
    user-select: text;

    &:hover {
      color: $black;
    }
  }
}

.status {
  font-family: Open Sans Bold, sans-serif;
  font-weight: bold;
  font-style: normal;
  font-size: 16px;
  line-height: 21px;
  color: $dark-blue;
  margin-top: 3px;
}

.date {
  font-family: Open Sans Semibold, sans-serif;
  font-weight: 600;
  font-style: normal;
  font-size: 14px;
  line-height: 18px;
  color: $gray;
  margin-top: 10px;
}

.text-container {
  width: 779px;

  ::v-deep a {
    color: $gray;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
}

.text {
  font-family: Open Sans Semibold, sans-serif;
  font-weight: 600;
  font-style: normal;
  font-size: 14px;
  line-height: 18px;
  color: $black;
}

@media only screen and (max-width: $screen-width-xs) {
  .content {
    width: 100%;
  }

  .card {
    flex-direction: column;
    padding: 20px 8px 0;
  }

  .title-container,
  .text-container {
    width: 100%;
  }

  .text {
    margin: 12px 0 22px;
  }
}
</style>
